import React, { useState } from 'react';
import {
  Card,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Stack,
  Chip,
  IconButton,
  Alert,
  LinearProgress,
  FormControlLabel,
  Switch,
  Grid,
  Tooltip,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { savingsAccountService } from 'src/services/savingsAccount.service';
import { ToastNotifications } from 'src/components';
import type { SavingsAccount, CreateSavingsAccountDTO } from 'src/services/savingsAccount.service';

interface SavingsAccountManagerProps {
  studentId: string;
  currentBalance: number;
}

const GOAL_TEMPLATES = [
  {
    name: 'College & Future Fund',
    description: 'Long-term savings for higher education and big future milestones.',
    targetAmount: 100,
    autoDepositPercentage: 15,
    isProtected: false,
    icon: 'solar:diploma-verified-bold-duotone',
  },
  {
    name: 'School Store Treat',
    description: 'Save up for items, books, and rewards from the classroom store.',
    targetAmount: 25,
    autoDepositPercentage: 5,
    isProtected: false,
    icon: 'solar:gift-bold-duotone',
  },
  {
    name: 'Tech & Class Reward',
    description: 'Stash away funds for games, events, and special class privileges.',
    targetAmount: 50,
    autoDepositPercentage: 10,
    isProtected: false,
    icon: 'solar:gamepad-bold-duotone',
  },
  {
    name: 'Rainy Day Safety Stash',
    description: 'Emergency reserve to protect yourself against fees and surprises.',
    targetAmount: 30,
    autoDepositPercentage: 5,
    isProtected: true,
    icon: 'solar:shield-check-bold-duotone',
  },
];

export default function SavingsAccountManager({
  studentId,
  currentBalance,
}: SavingsAccountManagerProps): React.JSX.Element {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [accountName, setAccountName] = useState('');
  const [accountDescription, setAccountDescription] = useState('');
  const [targetAmount, setTargetAmount] = useState<string>('');
  const [autoDepositPercentage, setAutoDepositPercentage] = useState<string>('');
  const [isProtected, setIsProtected] = useState(false);

  const [transferFromAccount, setTransferFromAccount] = useState<string>('balance');
  const [transferToAccount, setTransferToAccount] = useState<string>('');
  const [transferAmount, setTransferAmount] = useState<string>('');

  const queryClient = useQueryClient();

  // Fetch savings accounts
  const {
    data: savingsAccounts = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['savings-accounts', studentId],
    queryFn: () => savingsAccountService.getStudentSavingsAccounts(studentId),
    enabled: !!studentId,
  });

  // Fetch savings summary
  const { data: savingsSummary } = useQuery({
    queryKey: ['savings-summary', studentId],
    queryFn: () => savingsAccountService.getSavingsSummary(studentId),
    enabled: !!studentId,
  });

  // Mutations
  const createAccountMutation = useMutation({
    mutationFn: (accountData: CreateSavingsAccountDTO) =>
      savingsAccountService.createSavingsAccount(accountData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['savings-accounts', studentId] });
      queryClient.invalidateQueries({ queryKey: ['savings-summary', studentId] });
      ToastNotifications.success('Savings goal created!');
      handleCloseCreateModal();
    },
    onError: (err: any) => {
      ToastNotifications.error(err.message || 'Failed to create savings goal');
    },
  });

  const transferFundsMutation = useMutation({
    mutationFn: (transferData: any) => savingsAccountService.transferFunds(transferData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['savings-accounts', studentId] });
      queryClient.invalidateQueries({ queryKey: ['savings-summary', studentId] });
      queryClient.invalidateQueries({ queryKey: ['student-financial-summary', studentId] });
      ToastNotifications.success('Money moved successfully!');
      handleCloseTransferModal();
    },
    onError: (err: any) => {
      ToastNotifications.error(err.message || 'Failed to transfer money');
    },
  });

  const deleteAccountMutation = useMutation({
    mutationFn: (accountId: string) =>
      savingsAccountService.deleteSavingsAccount(accountId, studentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['savings-accounts', studentId] });
      queryClient.invalidateQueries({ queryKey: ['savings-summary', studentId] });
      ToastNotifications.success('Savings goal removed.');
    },
    onError: (err: any) => {
      ToastNotifications.error(err.message || 'Failed to delete savings goal');
    },
  });

  const handleCreateAccount = () => {
    if (!accountName.trim()) {
      ToastNotifications.error('Please enter a goal name');
      return;
    }

    const accountData: CreateSavingsAccountDTO = {
      student_id: studentId,
      name: accountName.trim(),
      description: accountDescription.trim() || undefined,
      target_amount: targetAmount ? parseFloat(targetAmount) : undefined,
      auto_deposit_percentage: autoDepositPercentage ? parseFloat(autoDepositPercentage) : undefined,
      is_protected: isProtected,
    };

    createAccountMutation.mutate(accountData);
  };

  const handleTransferFunds = () => {
    const amountVal = parseFloat(transferAmount);
    if (!transferAmount || isNaN(amountVal) || amountVal <= 0) {
      ToastNotifications.error('Please enter a valid amount');
      return;
    }

    if (!transferFromAccount) {
      ToastNotifications.error('Please select source account');
      return;
    }

    if (!transferToAccount) {
      ToastNotifications.error('Please select destination account');
      return;
    }

    if (transferFromAccount === transferToAccount) {
      ToastNotifications.error('Source and destination cannot be the same');
      return;
    }

    const fromAvail = getAvailableBalance(transferFromAccount);
    if (amountVal > fromAvail) {
      ToastNotifications.error(`Not enough funds. Available: $${fromAvail.toFixed(2)}`);
      return;
    }

    const transferData = {
      fromAccountId: transferFromAccount === 'balance' ? 'balance' : transferFromAccount,
      toAccountId: transferToAccount === 'balance' ? undefined : transferToAccount,
      amount: amountVal,
      studentId,
      reason: 'Manual student transfer',
    };

    transferFundsMutation.mutate(transferData);
  };

  const handleQuickDeposit = (account: SavingsAccount, amount: number) => {
    if (currentBalance < amount) {
      ToastNotifications.error(`Not enough in your Main Wallet. You have $${currentBalance.toFixed(2)}.`);
      return;
    }

    transferFundsMutation.mutate({
      fromAccountId: 'balance',
      toAccountId: account.id,
      amount,
      studentId,
      reason: `Quick deposit into ${account.name}`,
    });
  };

  const handleApplyTemplate = (template: typeof GOAL_TEMPLATES[0]) => {
    setAccountName(template.name);
    setAccountDescription(template.description);
    setTargetAmount(template.targetAmount.toString());
    setAutoDepositPercentage(template.autoDepositPercentage.toString());
    setIsProtected(template.isProtected);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setAccountName('');
    setAccountDescription('');
    setTargetAmount('');
    setAutoDepositPercentage('');
    setIsProtected(false);
  };

  const handleCloseTransferModal = () => {
    setIsTransferModalOpen(false);
    setTransferFromAccount('balance');
    setTransferToAccount('');
    setTransferAmount('');
  };

  const deleteAccount = (accountId: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete the "${name}" goal? Any remaining funds will be returned to your main wallet.`)) {
      deleteAccountMutation.mutate(accountId);
    }
  };

  const getAccountProgress = (account: SavingsAccount) => {
    if (!account.target_amount || account.target_amount <= 0) return 0;
    return Math.min(Math.round((Number(account.current_amount || 0) / account.target_amount) * 100), 100);
  };

  const getAvailableBalance = (accountId: string) => {
    if (accountId === 'balance') return currentBalance;
    const account = savingsAccounts.find((acc) => acc.id === accountId);
    return Number(account?.current_amount || 0);
  };

  const getVaultIcon = (name: string) => {
    const lower = name.toLowerCase();
    if (lower.includes('college') || lower.includes('grad')) return 'solar:diploma-verified-bold-duotone';
    if (lower.includes('store') || lower.includes('treat') || lower.includes('gift')) return 'solar:gift-bold-duotone';
    if (lower.includes('game') || lower.includes('tech') || lower.includes('reward')) return 'solar:gamepad-bold-duotone';
    if (lower.includes('rainy') || lower.includes('emergency') || lower.includes('safe')) return 'solar:shield-check-bold-duotone';
    return 'solar:wallet-money-bold-duotone';
  };

  if (error) {
    return (
      <Alert severity="error">
        Unable to load your savings goals. Please refresh the page.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Tab Content Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1.5}
        sx={{ mb: 2.5 }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1.0625rem' }}>
            Piggy Banks & Savings Goals
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem', mt: 0.25 }}>
            Set a goal, save for something you want, and watch your money grow.
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Icon icon="solar:transfer-horizontal-bold-duotone" width={16} />}
            onClick={() => setIsTransferModalOpen(true)}
            disabled={savingsAccounts.length === 0}
            sx={{
              height: 36,
              borderRadius: 1.5,
              fontWeight: 600,
              fontSize: '0.8125rem',
              textTransform: 'none',
              borderColor: 'divider',
              color: 'text.primary',
              flex: { xs: 1, sm: 'initial' },
              '&:hover': { bgcolor: 'grey.100', borderColor: 'text.secondary' },
            }}
          >
            Move Money
          </Button>

          <Button
            variant="contained"
            size="small"
            startIcon={<Icon icon="solar:add-circle-bold-duotone" width={16} />}
            onClick={() => setIsCreateModalOpen(true)}
            disabled={savingsAccounts.length >= 5}
            sx={{
              height: 36,
              borderRadius: 1.5,
              fontWeight: 700,
              fontSize: '0.8125rem',
              textTransform: 'none',
              bgcolor: '#059669',
              color: '#FFFFFF',
              flex: { xs: 1, sm: 'initial' },
              '&:hover': { bgcolor: '#047857' },
            }}
          >
            New Goal
          </Button>
        </Stack>
      </Stack>

      {/* Goal Cards Grid */}
      {isLoading ? (
        <Grid container spacing={2}>
          {[1, 2].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Card elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #E2E8F0', height: 180 }}>
                <LinearProgress sx={{ bgcolor: '#ECFDF5', '& .MuiLinearProgress-bar': { bgcolor: '#059669' } }} />
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : savingsAccounts.length === 0 ? (
        <Card
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 2.5,
            border: '1px dashed #CBD5E1',
            bgcolor: '#F8FAFC',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: '50%',
              bgcolor: 'rgba(5, 150, 105, 0.1)',
              color: '#059669',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 1.5,
            }}
          >
            <Icon icon="solar:wallet-money-bold-duotone" width={28} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1rem' }}>
            No Savings Goals Yet!
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8125rem', maxWidth: 400, mx: 'auto', mt: 0.5, mb: 2 }}>
            Start saving for something fun! Pick a goal like <strong>College Fund</strong> or <strong>School Store Treat</strong>.
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<Icon icon="solar:add-circle-bold-duotone" width={16} />}
            onClick={() => setIsCreateModalOpen(true)}
            sx={{
              borderRadius: 1.5,
              fontWeight: 700,
              fontSize: '0.8125rem',
              textTransform: 'none',
              bgcolor: '#059669',
              '&:hover': { bgcolor: '#047857' },
            }}
          >
            Create Your First Goal
          </Button>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {savingsAccounts.map((account) => {
            const current = Number(account.current_amount || 0);
            const target = Number(account.target_amount || 0);
            const progress = getAccountProgress(account);
            const vaultIcon = getVaultIcon(account.name);

            return (
              <Grid item xs={12} sm={6} md={4} key={account.id}>
                <Card
                  elevation={0}
                  sx={{
                    p: 2.25,
                    borderRadius: 2.5,
                    border: '1px solid #E2E8F0',
                    bgcolor: '#FFFFFF',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      borderColor: '#CBD5E1',
                      boxShadow: '0 4px 14px rgba(15, 23, 42, 0.05)',
                    },
                  }}
                >
                  {/* Top: Icon, Title & Delete Button */}
                  <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
                      <Stack direction="row" alignItems="center" spacing={1.25}>
                        <Box
                          sx={{
                            width: 38,
                            height: 38,
                            borderRadius: 1.75,
                            bgcolor: 'rgba(5, 150, 105, 0.1)',
                            color: '#059669',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <Icon icon={vaultIcon} width={22} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.9375rem' }} noWrap>
                            {account.name}
                          </Typography>
                          {account.description && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block' }} noWrap>
                              {account.description}
                            </Typography>
                          )}
                        </Box>
                      </Stack>

                      <Tooltip title="Delete Goal">
                        <IconButton
                          size="small"
                          onClick={() => deleteAccount(account.id, account.name)}
                          sx={{ color: 'text.secondary', p: 0.5, '&:hover': { color: 'error.main' } }}
                        >
                          <Icon icon="solar:trash-bin-trash-bold" width={16} />
                        </IconButton>
                      </Tooltip>
                    </Stack>

                    {/* Progress Bar & Amount */}
                    <Box sx={{ my: 1.5 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.75 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#059669', fontSize: '1.25rem' }}>
                          ${current.toFixed(2)}
                        </Typography>
                        {target > 0 && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.75rem' }}>
                            of ${target.toFixed(2)} ({progress}%)
                          </Typography>
                        )}
                      </Stack>

                      {target > 0 && (
                        <LinearProgress
                          variant="determinate"
                          value={progress}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            bgcolor: '#ECFDF5',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: '#059669',
                              borderRadius: 4,
                            },
                          }}
                        />
                      )}
                    </Box>

                    {/* Badges: Auto-Save / Protected */}
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                      {account.auto_deposit_percentage > 0 && (
                        <Chip
                          icon={<Icon icon="solar:bolt-bold-duotone" width={12} />}
                          label={`Auto-saves ${account.auto_deposit_percentage}% of pay`}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.625rem',
                            fontWeight: 600,
                            bgcolor: 'rgba(5, 150, 105, 0.08)',
                            color: '#059669',
                            borderRadius: 0.75,
                          }}
                        />
                      )}
                      {account.is_protected && (
                        <Chip
                          icon={<Icon icon="solar:shield-check-bold" width={12} />}
                          label="Protected Vault"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.625rem',
                            fontWeight: 600,
                            bgcolor: '#F1F5F9',
                            color: '#475569',
                            borderRadius: 0.75,
                          }}
                        />
                      )}
                    </Stack>
                  </Box>

                  {/* Bottom: Quick Transfer Shortcuts */}
                  <Box sx={{ pt: 1.5, borderTop: '1px solid #F1F5F9' }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.6875rem' }}>
                        Quick Stash:
                      </Typography>
                      <Stack direction="row" spacing={0.75}>
                        <Button
                          size="small"
                          onClick={() => handleQuickDeposit(account, 5)}
                          disabled={currentBalance < 5}
                          sx={{
                            height: 26,
                            px: 1.25,
                            fontSize: '0.6875rem',
                            fontWeight: 700,
                            borderRadius: 1,
                            bgcolor: '#F1F5F9',
                            color: '#334155',
                            minWidth: 0,
                            '&:hover': { bgcolor: 'rgba(5, 150, 105, 0.1)', color: '#059669' },
                          }}
                        >
                          +$5
                        </Button>
                        <Button
                          size="small"
                          onClick={() => handleQuickDeposit(account, 10)}
                          disabled={currentBalance < 10}
                          sx={{
                            height: 26,
                            px: 1.25,
                            fontSize: '0.6875rem',
                            fontWeight: 700,
                            borderRadius: 1,
                            bgcolor: '#F1F5F9',
                            color: '#334155',
                            minWidth: 0,
                            '&:hover': { bgcolor: 'rgba(5, 150, 105, 0.1)', color: '#059669' },
                          }}
                        >
                          +$10
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Create Goal Modal */}
      <Dialog
        open={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  bgcolor: 'rgba(5, 150, 105, 0.12)',
                  color: '#059669',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon icon="solar:wallet-money-bold-duotone" width={20} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.125rem' }}>
                Create a Savings Goal
              </Typography>
            </Stack>
            <IconButton size="small" onClick={handleCloseCreateModal}>
              <Icon icon="solar:close-circle-bold" width={20} />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          {/* Preset Goal Templates */}
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', mb: 1, display: 'block' }}>
            Choose a Goal Template:
          </Typography>

          <Grid container spacing={1} sx={{ mb: 2.5 }}>
            {GOAL_TEMPLATES.map((tmpl) => (
              <Grid item xs={12} sm={6} key={tmpl.name}>
                <Card
                  elevation={0}
                  onClick={() => handleApplyTemplate(tmpl)}
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: accountName === tmpl.name ? '#059669' : '#E2E8F0',
                    bgcolor: accountName === tmpl.name ? 'rgba(5, 150, 105, 0.08)' : '#F8FAFC',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    '&:hover': { borderColor: '#059669', transform: 'translateY(-1px)' },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ color: '#059669', display: 'flex' }}>
                      <Icon icon={tmpl.icon} width={18} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8125rem', color: 'text.primary' }} noWrap>
                        {tmpl.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
                        Target: ${tmpl.targetAmount} · Save {tmpl.autoDepositPercentage}%
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Goal Configuration Fields */}
          <Stack spacing={2}>
            <TextField
              label="Goal Name"
              placeholder="e.g. Graduation Trip, Class Store Reward"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              fullWidth
              size="small"
              required
            />

            <TextField
              label="Short Note / Reason"
              placeholder="What are you saving for?"
              value={accountDescription}
              onChange={(e) => setAccountDescription(e.target.value)}
              fullWidth
              size="small"
            />

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Target Goal Amount ($D)"
                  placeholder="e.g. 50"
                  type="number"
                  value={targetAmount}
                  onChange={(e) => setTargetAmount(e.target.value)}
                  fullWidth
                  size="small"
                  InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary', fontWeight: 600 }}>$</Typography> }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Auto-Save from Paychecks (%)"
                  placeholder="e.g. 10"
                  type="number"
                  value={autoDepositPercentage}
                  onChange={(e) => setAutoDepositPercentage(e.target.value)}
                  fullWidth
                  size="small"
                  InputProps={{ endAdornment: <Typography sx={{ ml: 0.5, color: 'text.secondary', fontWeight: 600 }}>%</Typography> }}
                />
              </Grid>
            </Grid>

            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
              <FormControlLabel
                control={<Switch checked={isProtected} onChange={(e) => setIsProtected(e.target.checked)} color="primary" />}
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                      Protected Vault (Lock Stash)
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                      Locks funds so they cannot be accidentally spent on daily store purchases.
                    </Typography>
                  </Box>
                }
              />
            </Box>

            <Button
              variant="contained"
              onClick={handleCreateAccount}
              disabled={createAccountMutation.isPending || !accountName.trim()}
              fullWidth
              sx={{
                py: 1.25,
                borderRadius: 1.75,
                fontWeight: 700,
                fontSize: '0.875rem',
                textTransform: 'none',
                bgcolor: '#059669',
                '&:hover': { bgcolor: '#047857' },
              }}
            >
              {createAccountMutation.isPending ? 'Creating Goal...' : 'Create Savings Goal'}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Move Money Transfer Modal */}
      <Dialog
        open={isTransferModalOpen}
        onClose={handleCloseTransferModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  bgcolor: 'rgba(5, 150, 105, 0.12)',
                  color: '#059669',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon icon="solar:transfer-horizontal-bold-duotone" width={20} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.125rem' }}>
                Move Money
              </Typography>
            </Stack>
            <IconButton size="small" onClick={handleCloseTransferModal}>
              <Icon icon="solar:close-circle-bold" width={20} />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2}>
            {/* From Account */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', mb: 0.5, display: 'block' }}>
                FROM:
              </Typography>
              <TextField
                select
                fullWidth
                size="small"
                value={transferFromAccount}
                onChange={(e) => setTransferFromAccount(e.target.value)}
                SelectProps={{ native: true }}
              >
                <option value="balance">Main Wallet (Available: ${currentBalance.toFixed(2)})</option>
                {savingsAccounts.map((acc) => (
                  <option key={acc.id} value={acc.id}>
                    {acc.name} (${Number(acc.current_amount || 0).toFixed(2)})
                  </option>
                ))}
              </TextField>
            </Box>

            {/* To Account */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', mb: 0.5, display: 'block' }}>
                TO:
              </Typography>
              <TextField
                select
                fullWidth
                size="small"
                value={transferToAccount}
                onChange={(e) => setTransferToAccount(e.target.value)}
                SelectProps={{ native: true }}
              >
                <option value="">Select Destination Goal...</option>
                <option value="balance">Main Wallet (${currentBalance.toFixed(2)})</option>
                {savingsAccounts.map((acc) => (
                  <option key={acc.id} value={acc.id}>
                    {acc.name} (${Number(acc.current_amount || 0).toFixed(2)})
                  </option>
                ))}
              </TextField>
            </Box>

            {/* Amount */}
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', mb: 0.5, display: 'block' }}>
                AMOUNT TO MOVE:
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="0.00"
                value={transferAmount}
                onChange={(e) => setTransferAmount(e.target.value)}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary', fontWeight: 600 }}>$</Typography>,
                }}
              />

              {/* Quick Amount Chips */}
              <Stack direction="row" spacing={0.75} sx={{ mt: 1 }}>
                {[1, 5, 10, 25].map((amt) => (
                  <Button
                    key={amt}
                    size="small"
                    onClick={() => setTransferAmount(amt.toString())}
                    sx={{
                      height: 26,
                      fontSize: '0.6875rem',
                      fontWeight: 700,
                      bgcolor: '#F1F5F9',
                      color: '#334155',
                      borderRadius: 1,
                      minWidth: 0,
                      px: 1.25,
                      '&:hover': { bgcolor: 'rgba(5, 150, 105, 0.1)', color: '#059669' },
                    }}
                  >
                    +${amt}
                  </Button>
                ))}
                <Button
                  size="small"
                  onClick={() => setTransferAmount(getAvailableBalance(transferFromAccount).toString())}
                  sx={{
                    height: 26,
                    fontSize: '0.6875rem',
                    fontWeight: 700,
                    bgcolor: '#F1F5F9',
                    color: '#334155',
                    borderRadius: 1,
                    minWidth: 0,
                    px: 1.25,
                    '&:hover': { bgcolor: 'rgba(5, 150, 105, 0.1)', color: '#059669' },
                  }}
                >
                  All
                </Button>
              </Stack>
            </Box>

            <Button
              variant="contained"
              onClick={handleTransferFunds}
              disabled={transferFundsMutation.isPending || !transferAmount || !transferToAccount}
              fullWidth
              sx={{
                py: 1.25,
                borderRadius: 1.75,
                fontWeight: 700,
                fontSize: '0.875rem',
                textTransform: 'none',
                bgcolor: '#059669',
                '&:hover': { bgcolor: '#047857' },
              }}
            >
              {transferFundsMutation.isPending ? 'Moving Money...' : 'Confirm Move'}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}